import { useCallback, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Plus, X, Pencil, Trash2 } from 'lucide-react'
import { useNotes, useCreateNote, useUpdateNote, useDeleteNote } from '../../api/hooks'
import { useUIStore } from '../../store/ui'
import { useToast } from '../primitives/Toast'
import { noteTitle, notePreview, formatNoteTime } from './noteMeta'

/**
 * The notes list — a second sidebar that opens to the right of the main one and
 * stays open while the user moves between notes.
 */
export function NotesListPanel() {
  const { id: activeId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const closePanel = useUIStore((s) => s.closeNotesPanel)
  const { data: notes = [], isLoading } = useNotes()
  const createNote = useCreateNote()
  const updateNote = useUpdateNote()
  const deleteNote = useDeleteNote()
  const { showToast } = useToast()

  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')

  const handleCreate = useCallback(() => {
    createNote.mutate(
      {},
      {
        onSuccess: (note) => navigate(`/notes/${note.id}`),
        onError: () => showToast('Failed to create note', 'error'),
      }
    )
  }, [createNote, navigate, showToast])

  const commitRename = useCallback(() => {
    if (!renamingId) return
    const id = renamingId
    const title = renameValue.trim()
    setRenamingId(null)
    updateNote.mutate(
      { id, title },
      { onError: () => showToast('Failed to rename note', 'error') }
    )
  }, [renamingId, renameValue, updateNote, showToast])

  const handleDelete = useCallback(
    (id: string, title: string) => {
      if (!window.confirm(`Delete "${title}"?`)) return
      deleteNote.mutate(id, {
        onSuccess: () => {
          if (activeId === id) navigate('/notes')
        },
        onError: () => showToast('Failed to delete note', 'error'),
      })
    },
    [deleteNote, activeId, navigate, showToast]
  )

  return (
    <aside
      style={{
        width: '260px',
        minWidth: '260px',
        borderRight: '1px solid var(--color-border)',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--color-paper)',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          padding: '20px 14px 12px 18px',
        }}
      >
        <span className="eyebrow">Notes</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
          <IconButton label="New note" onClick={handleCreate}>
            <Plus size={14} strokeWidth={1.5} />
          </IconButton>
          <IconButton label="Close notes panel" onClick={closePanel}>
            <X size={14} strokeWidth={1.5} />
          </IconButton>
        </div>
      </div>

      <hr className="hairline" style={{ margin: 0 }} />

      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0 12px' }}>
        {isLoading && <PanelMessage>Loading…</PanelMessage>}

        {!isLoading && notes.length === 0 && (
          <PanelMessage>No notes yet. Start one with +.</PanelMessage>
        )}

        {notes.map((note) => {
          const isActive = note.id === activeId
          const title = noteTitle(note)

          return (
            <div
              key={note.id}
              className="notes-row"
              onClick={() => navigate(`/notes/${note.id}`)}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '6px',
                padding: '10px 12px 10px 18px',
                cursor: 'pointer',
                background: isActive ? 'var(--color-paper-dark)' : 'transparent',
                borderLeft: `2px solid ${isActive ? 'var(--color-accent)' : 'transparent'}`,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                {renamingId === note.id ? (
                  <input
                    value={renameValue}
                    autoFocus
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onBlur={commitRename}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitRename()
                      if (e.key === 'Escape') setRenamingId(null)
                    }}
                    style={{
                      width: '100%',
                      background: 'transparent',
                      border: 'none',
                      borderBottom: '1px solid var(--color-accent)',
                      padding: '2px 0',
                      fontFamily: 'var(--font-serif)',
                      fontSize: '0.9375rem',
                      color: 'var(--color-ink)',
                      outline: 'none',
                    }}
                  />
                ) : (
                  <div
                    style={{
                      fontFamily: 'var(--font-serif)',
                      fontSize: '0.9375rem',
                      fontWeight: isActive ? 500 : 400,
                      color: isActive ? 'var(--color-ink)' : 'var(--color-ink-muted)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {title}
                  </div>
                )}
                <div
                  style={{
                    marginTop: '2px',
                    fontFamily: 'var(--font-serif)',
                    fontSize: '0.75rem',
                    color: 'var(--color-ink-faint)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatNoteTime(note.updated_at)} · {notePreview(note)}
                </div>
              </div>

              <div className="notes-row-actions" style={{ display: 'flex', gap: '2px', opacity: 0 }}>
                <IconButton
                  label={`Rename ${title}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    setRenamingId(note.id)
                    setRenameValue(note.title)
                  }}
                >
                  <Pencil size={12} strokeWidth={1.5} />
                </IconButton>
                <IconButton
                  label={`Delete ${title}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDelete(note.id, title)
                  }}
                >
                  <Trash2 size={12} strokeWidth={1.5} />
                </IconButton>
              </div>
            </div>
          )
        })}
      </div>

      <style>{`
        .notes-row:hover { background: var(--color-paper-dark); }
        .notes-row:hover .notes-row-actions { opacity: 0.5; }
        .notes-row-actions button:hover { opacity: 1; color: var(--color-ink) !important; }
      `}</style>
    </aside>
  )
}

function PanelMessage({ children }: { children: React.ReactNode }) {
  return (
    <p
      style={{
        margin: 0,
        padding: '14px 18px',
        fontFamily: 'var(--font-serif)',
        fontStyle: 'italic',
        fontSize: '0.8125rem',
        color: 'var(--color-ink-faint)',
      }}
    >
      {children}
    </p>
  )
}

interface IconButtonProps {
  label: string
  onClick: (e: React.MouseEvent) => void
  children: React.ReactNode
}

function IconButton({ label, onClick, children }: IconButtonProps) {
  return (
    <button
      onClick={onClick}
      title={label}
      aria-label={label}
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: 'var(--color-ink-faint)',
        padding: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {children}
    </button>
  )
}
